<script lang="ts" setup>
// The upstream transactions this one came from, in run order: the first service on top, the direct parent last.
// Opening a line reads what this transaction received from that ancestor.
import { computed, ref } from "vue";
import type { ContentPreview, TransactionParentRef } from "@/types";
import TransactionBody from "./TransactionBody.vue";

const props = defineProps<{
  parents: TransactionParentRef[];
  serviceNames: Record<string, string>;
  runId: string;
}>();

// One line per service. `parent` is that service's first parent in run order; `count` is how many parents came
// from it, and a line with more than one does not open.
interface InputLine {
  parent: TransactionParentRef;
  count: number;
}

// The parents arrive nearest first, so they are reversed into run order before grouping.
const lines = computed(() => {
  const byService = new Map<string, InputLine>();
  for (const parent of [...props.parents].reverse()) {
    const line = byService.get(parent.serviceId);
    if (line) line.count++;
    else byService.set(parent.serviceId, { parent, count: 1 });
  }
  return [...byService.values()];
});

// Keyed by service id, which is unique among the lines that open. What a line loaded is handed back to its body
// when it opens again, so each ancestor is read once.
const open = ref<Record<string, boolean>>({});
const loaded = ref<Record<string, ContentPreview>>({});

function toggle(serviceId: string) {
  open.value[serviceId] = !open.value[serviceId];
}

function elementText(parent: TransactionParentRef): string {
  const element = parent.outputIndex + 1;
  const elements = loaded.value[parent.serviceId]?.elements;
  if (elements != null) return `element ${element} of ${elements}`;
  if (parent.outputIndex > 0) return `element ${element}`;
  return "";
}
</script>

<template>
  <div class="small">
    <template v-for="{ parent, count } in lines" :key="parent.serviceId">
      <div
        v-if="count > 1"
        class="transaction-input-line d-flex flex-wrap align-items-center gap-2 py-1 px-1"
        :data-line="parent.serviceId"
      >
        <span class="transaction-input-chevron"></span>
        <span class="badge bg-body-secondary text-body-secondary font-monospace fw-normal">{{ parent.serviceId }}</span>
        <span v-if="serviceNames[parent.serviceId]">{{ serviceNames[parent.serviceId] }}</span>
        <small class="text-body-secondary">{{ count }} transactions</small>
      </div>
      <template v-else>
        <div
          class="transaction-input-line transaction-input-openable d-flex flex-wrap align-items-center gap-2 py-1 px-1"
          :data-line="parent.serviceId"
          role="button"
          tabindex="0"
          :aria-expanded="!!open[parent.serviceId]"
          @click="toggle(parent.serviceId)"
          @keydown.enter.prevent="toggle(parent.serviceId)"
          @keydown.space.prevent="toggle(parent.serviceId)"
        >
          <i
            class="bi text-body-secondary transaction-input-chevron"
            :class="open[parent.serviceId] ? 'bi-chevron-down' : 'bi-chevron-right'"
          ></i>
          <span class="badge bg-body-secondary text-body-secondary font-monospace fw-normal">{{ parent.serviceId }}</span>
          <span v-if="serviceNames[parent.serviceId]">{{ serviceNames[parent.serviceId] }}</span>
          <small v-if="elementText(parent)" class="text-body-secondary">{{ elementText(parent) }}</small>
        </div>
        <div v-if="open[parent.serviceId]" class="transaction-input-body">
          <TransactionBody
            :body="loaded[parent.serviceId]"
            :source="{ output: parent.outputIndex }"
            :run-id="runId"
            :service-id="parent.serviceId"
            :transaction-id="parent.transactionId"
            @loaded="loaded[parent.serviceId] = $event"
          />
        </div>
      </template>
    </template>
  </div>
</template>

<style scoped>
.transaction-input-line:not(:first-child) { border-top: 1px solid var(--bs-border-color); }
.transaction-input-openable { cursor: pointer; }
.transaction-input-openable:hover { background-color: var(--bs-tertiary-bg); }
.transaction-input-chevron { width: 1rem; flex-shrink: 0; }
/* The line's padding (0.25rem), the chevron (1rem) and the gap (0.5rem) put the badge 1.75rem in, so the body
   starts under the badge. */
.transaction-input-body { padding: 0.25rem 0 0.75rem 1.75rem; }
</style>
